import {useState, useRef, useEffect, useCallback, memo, useMemo} from 'react'
import { apiFetch } from '@/lib/api'
import Button from './ui/Button'
import { FiLoader, FiPlay, FiCheckCircle, FiXCircle, FiDownload, FiPlus, FiX, FiMessageSquare, FiTrash2, FiFilm, FiImage, FiUpload, FiChevronsLeft, FiChevronsRight, FiMoreHorizontal, FiCopy } from 'react-icons/fi'
import { LuAudioLines, LuBrainCircuit } from 'react-icons/lu'
import { FaPlay } from "react-icons/fa";
import { useShow } from '@/contexts/ShowContext'
import { useModal } from '@/contexts/ModalContext'
import { useWebSocketContext } from '@/contexts/WebSocketContext'
import { useWebSocket } from '@/hooks/useWebSocket'
import TaskModal from '@/components/modals/TaskModal'
import AssetEditorModal from '@/components/modals/AssetEditorModal'
import ConfirmModal from '@/components/modals/ConfirmModal'
import ImagePreviewModal from '@/components/modals/ImagePreviewModal'
import VideoPreviewModal from '@/components/modals/VideoPreviewModal'
import AudioPreviewModal from '@/components/modals/AudioPreviewModal'
import { AssetPicker } from '@/components/AiModelEditor'
import { toast } from 'sonner'
import { FaArrowUp } from "react-icons/fa";
import { LuLoaderCircle } from "react-icons/lu";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger} from '@/components/ui/shadcn/dropdown-menu'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LuLetterText } from 'react-icons/lu'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { CiViewTimeline } from "react-icons/ci";


const MAX_CUSTOM_ASSETS = 6

const TERMINAL_STATES = new Set(['completed', 'failed', 'cancelled'])

function dbMessageToUiMessage(msg) {
    const base = { id: msg._id, type: msg.type }
    if (msg.type === 'user') return { ...base, role: 'user', text: msg.text || '' }
    if (msg.type === 'assistant') return { ...base, role: 'assistant', text: msg.text || '' }
    if (msg.type === 'task') return { ...base, role: 'assistant', task: msg.task }
    if (msg.type === 'generation') {
        const gen = msg.generation
        return { ...base, role: 'assistant', generation: gen, _generationGroup: gen?.group || null }
    }
    if (msg.type === 'error') return { ...base, id: msg._id || `error-${Date.now()}`, role: 'assistant', error: msg.error || 'An unknown error occurred' }
    return { ...base, role: 'assistant', text: msg.text || '' }
}

function groupGenerationMessages(messages) {
    const flat = []
    for (const msg of messages) {
        if (msg.type === 'generations' && msg.generations) {
            for (const gen of msg.generations) {
                flat.push({ id: gen._id, type: 'generation', role: 'assistant', generation: gen, _generationGroup: msg._generationGroup })
            }
        } else {
            flat.push(msg)
        }
    }

    const result = []
    const groupMap = new Map()

    for (const msg of flat) {
        const groupId = msg._generationGroup
        if (msg.type === 'generation' && msg.generation && groupId) {
            if (groupMap.has(groupId)) {
                const grouped = groupMap.get(groupId)
                grouped.generations = [...grouped.generations, msg.generation]
            } else {
                const grouped = { id: msg.id, type: 'generations', role: 'assistant', generations: [msg.generation], _generationGroup: groupId }
                groupMap.set(groupId, grouped)
                result.push(grouped)
            }
        } else {
            result.push(msg)
        }
    }
    return result
}

// ---------------------------------------------------------------------------
// Media type detection
// ---------------------------------------------------------------------------

const MEDIA_EXTENSIONS = {
    image: new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff']),
    video: new Set(['mp4', 'webm', 'mov', 'avi', 'mkv', 'ogv']),
    audio: new Set(['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma']),
}

function getMediaType(url) {
    try {
        const parsed = new URL(url)
        const ext = parsed.pathname.split('.').pop()?.toLowerCase()
        if (ext && MEDIA_EXTENSIONS.image.has(ext)) return 'image'
        if (ext && MEDIA_EXTENSIONS.video.has(ext)) return 'video'
        if (ext && MEDIA_EXTENSIONS.audio.has(ext)) return 'audio'
        const ctParam = (parsed.searchParams.get('content_type') || parsed.searchParams.get('type') || '').toLowerCase()
        if (ctParam.startsWith('video')) return 'video'
        if (ctParam.startsWith('audio')) return 'audio'
        if (ctParam.startsWith('image')) return 'image'
    } catch { /* not a valid URL */ }
    return null
}

// ---------------------------------------------------------------------------
// Inline media thumbnail (used inside markdown renderer)
// ---------------------------------------------------------------------------

function MediaThumbnail({ url, mediaType: initialMediaType, onPreview }) {
    const [mediaType, setMediaType] = useState(initialMediaType)

    const handleImgError = useCallback(() => {
        if (mediaType === 'image') setMediaType('video')
    }, [mediaType])

    const handleClick = useCallback(() => {
        onPreview?.(url, mediaType)
    }, [onPreview, url, mediaType])

    return (
        <span
            className="inline-block align-middle m-0.5 w-10 h-10 rounded-full overflow-hidden border cursor-pointer hover:opacity-80"
            onClick={handleClick}>
                {mediaType === 'image' && (
                    <img src={url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" draggable={false} onError={handleImgError} />
                )}
                {mediaType === 'video' && (
                    <span className="relative block w-full h-full">
                        <video src={url} muted preload="metadata" className="w-full h-full object-cover" draggable={false} />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <FaPlay className="w-3 h-3 text-secondary-foreground/80" />
                        </span>
                    </span>
                )}
                {mediaType === 'audio' && (
                    <span className="flex items-center justify-center bg-secondary/30 w-full h-full">
                        <LuAudioLines className="w-4 h-4 text-secondary-foreground/80 group-hover:hidden" />
                    </span>
                )}
        </span>
    )
}

// ---------------------------------------------------------------------------
// Markdown-rendered assistant message with inline media previews
// ---------------------------------------------------------------------------

const REMARK_PLUGINS = [remarkGfm, remarkBreaks]

function collapseListBlanks(md) {
    return md.replace(/(\n\s*\n)(?=[ \t]*(?:[-*+]|\d+[.)]) )/g, '\n')
}

function AssistantMessage({ text }) {
    const { openModal } = useModal()

    const cleanedText = useMemo(() => {
        let md = collapseListBlanks(text || '')
        md = md.replace(/\n{2,}/g, (match) => '\n' + '&nbsp;\n'.repeat(match.length - 1))
        return md
    }, [text])

    const handlePreview = useCallback((url, mediaType) => {
        const asset = { url, thumbnail_url: url, name: '', mediaType }
        if (mediaType === 'video') openModal(<VideoPreviewModal video={asset} />)
        else if (mediaType === 'audio') openModal(<AudioPreviewModal audio={asset} />)
        else openModal(<ImagePreviewModal image={asset} />)
    }, [openModal])

    const components = useMemo(() => ({
        a({ href, children }) {
            const mediaType = href ? getMediaType(href) : null
            if (mediaType) {
                return <MediaThumbnail url={href} mediaType={mediaType} onPreview={handlePreview} />
            }
            return (
                <a href={href} target="_blank" rel="noopener noreferrer"
                    className="text-primary underline break-all cursor-pointer">
                    {children}
                </a>
            )
        },
        img({ src }) {
            const mediaType = src ? getMediaType(src) : 'image'
            return <MediaThumbnail url={src} mediaType={mediaType || 'image'} onPreview={handlePreview} />
        },
        p({ children }) { return <p className="my-1">{children}</p> },
        h1({ children }) { return <h1 className="text-base font-bold mt-3 mb-1">{children}</h1> },
        h2({ children }) { return <h2 className="text-sm font-bold mt-2.5 mb-1">{children}</h2> },
        h3({ children }) { return <h3 className="text-sm font-semibold mt-2 mb-0.5">{children}</h3> },
        ul({ children }) { return <ul className="list-disc list-inside my-1 space-y-0.5">{children}</ul> },
        ol({ children }) { return <ol className="list-decimal list-inside my-1 space-y-0.5">{children}</ol> },
        li({ children }) { return <li>{children}</li> },
        code({ className, children, ...props }) {
            const isBlock = className?.includes('language-')
            if (isBlock) {
                return (
                    <code className="block bg-secondary/40 rounded-md p-2 my-1.5 text-[0.75rem] overflow-x-auto font-mono whitespace-pre" {...props}>
                        {children}
                    </code>
                )
            }
            return <code className="bg-secondary/40 rounded px-1 py-0.5 text-[0.75rem] font-mono" {...props}>{children}</code>
        },
        pre({ children }) { return <pre className="my-1.5">{children}</pre> },
        blockquote({ children }) {
            return <blockquote className="border-l-2 border-primary/40 pl-3 my-1.5 text-secondary-foreground/70 italic">{children}</blockquote>
        },
        table({ children }) {
            return <div className="overflow-x-auto my-1.5"><table className="text-xs border-collapse">{children}</table></div>
        },
        th({ children }) { return <th className="border border-secondary/60 px-2 py-1 text-left font-semibold bg-secondary/20">{children}</th> },
        td({ children }) { return <td className="border border-secondary/60 px-2 py-1">{children}</td> },
        hr() { return <hr className="border-secondary/40 my-2" /> },
        strong({ children }) { return <strong className="font-semibold">{children}</strong> },
    }), [handlePreview])

    return (
        <div className="break-words text-[0.8rem] leading-relaxed">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
                {cleanedText}
            </ReactMarkdown>
        </div>
    )
}

// ---------------------------------------------------------------------------
// Message rendering
// ---------------------------------------------------------------------------

const ChatMessage = memo(function ChatMessage({ message, onNewChat }) {
    if (message.type === 'user') {
        return (
            <div className="flex gap-4 justify-end">
                <div className="max-w-[80%] rounded-xl rounded-br-none p-2 bg-primary text-primary-foreground">
                    <div className="whitespace-pre-wrap break-words text-[0.8rem]">
                        {message.text}
                    </div>
                </div>
            </div>
        )
    }

    let content = null
    if (message.type === 'assistant') {
        content = <AssistantMessage text={message.text} />
    } else if (message.type === 'generations') {
        content = <GenerationsPart generations={message.generations} onNewChat={onNewChat} />
    } else if (message.type === 'generation' && message.generation) {
        content = <GenerationsPart generations={[message.generation]} onNewChat={onNewChat} />
    } else if (message.type === 'task') {
        content = <TaskPart taskId={message.task?._id} initialTask={message.task} />
    } else if (message.type === 'error') {
        content = (
            <div className="flex items-center gap-2 text-[0.7rem] text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2.5 py-2">
                <FiXCircle className="w-3 h-3 shrink-0" />
                <span className="break-all whitespace-pre-wrap">{message.error}</span>
            </div>
        )
    }

    if (!content) return null

    return (
        <div className="flex gap-4 justify-start">
            <div className="max-w-[80%] flex flex-col gap-1">
                <div className="text-secondary-foreground flex flex-col gap-2">
                    {content}
                </div>
            </div>
        </div>
    )
})

function GenerationsPart({ generations, onNewChat }) {
    const { openModal } = useModal()
    const { show } = useShow()
    const navigate = useNavigate()

    const handlePreview = (gen) => {
        if (!gen?.url) return
        const asset = { url: gen.url, thumbnail_url: gen.thumbnail_url || gen.url, name: gen.prompt || '', mediaType: gen.type }
        if (gen.type === 'video') openModal(<VideoPreviewModal video={asset} />)
        else if (gen.type === 'audio') openModal(<AudioPreviewModal audio={asset} />)
        else openModal(<ImagePreviewModal image={asset} />)
    }

    const handleDownload = async (gen) => {
        if (!gen?.url) return
        const blob = await fetch(gen.url).then(res => res.blob())
        const mimeType = blob.type
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `asset_${Date.now()}.${mimeType.split('/')[1]}`
        link.click()
        URL.revokeObjectURL(url)
    }

    const handleSaveAsset = async (gen) => {
        if (!gen?.url) return
        const newAsset = await openModal(<AssetEditorModal asset={{
            url: gen.url,
            thumbnail_url: gen.thumbnail_url || gen.url,
            name: 'New Asset',
            description: gen.prompt,
            mediaType: gen.type,
            source: 'studio',
        }} />, 'side')
        if (!newAsset) return
        try {
            const response = await apiFetch(`/api/shows/${show._id}/assets`, {
                method: 'POST',
                body: JSON.stringify({
                    _id: gen._id,
                    url: gen.url,
                    thumbnail_url: gen.thumbnail_url || gen.url,
                    name: newAsset.name,
                    description: newAsset.description,
                    mediaType: gen.type,
                    source: 'studio',
                }),
            })
            if (!response.ok) throw new Error('Failed to save generation')
            toast('Done!', {
                description: `Asset "${newAsset.name}" has been saved`,
                duration: 5000,
                position: 'top-center',
                action: { label: 'View', onClick: () => { navigate('/show/assets') } },
            })
        } catch (error) {
            console.error('Error saving generation:', error)
        }
    }

    const typeIcon = (type) => {
        if (type === 'audio') return <LuAudioLines className="w-3 h-3" />
        if (type === 'video') return <span className="text-[9px] font-bold uppercase">vid</span>
        return <span className="text-[9px] font-bold uppercase">img</span>
    }

    return (
        <div className={`grid grid-cols-2 gap-1 rounded overflow-hidden`}>
            {generations.map((gen) => (
                <div key={gen._id} className="relative flex-none h-auto select-none group/gen">
                    {gen.url ? (
                        <div className="w-full h-auto relative cursor-pointer overflow-hidden border rounded-md" onClick={() => handlePreview(gen)}>
                            {gen.type === 'image' && <img src={gen.thumbnail_url || gen.url} alt={gen.prompt || 'Generated image'} loading="lazy" decoding="async" className="object-contain w-full h-auto" draggable={false} />}
                            {gen.type === 'video' && (
                                gen.thumbnail_url
                                    ? <img src={gen.thumbnail_url} alt={gen.prompt || 'Generated video'} loading="lazy" decoding="async" className="object-contain w-full h-auto" draggable={false} />
                                    : <video src={gen.url} muted className="object-contain w-full h-auto" draggable={false} />
                            )}
                            {gen.type === 'audio' && (
                                <div className="w-full aspect-video flex items-center justify-center min-w-50">
                                    <LuAudioLines className="w-8 h-8 text-secondary-foreground/80" />
                                </div>
                            )}
                            <div className="absolute top-0 left-0 w-full h-full bg-background/50 hidden items-center justify-center pointer-events-none group-hover/gen:flex">
                                <div className="absolute bottom-0 right-0 p-1 pointer-events-auto">
                                    <div className="flex items-center bg-card rounded-lg border">
                                        {onNewChat && (
                                            <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); onNewChat([{ url: gen.url, type: gen.type, name: gen.prompt || '' }]) }}>
                                                <LuBrainCircuit className="w-3 h-3" />
                                            </div>
                                        )}
                                        <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); handleSaveAsset(gen) }}>
                                            <FiPlus className="w-3 h-3" />
                                        </div>
                                        <div className="p-1.5 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); handleDownload(gen) }}>
                                            <FiDownload className="w-3 h-3" />
                                        </div>
                                        
                                    </div>
                                </div>
                            </div>
                            {gen.prompt && (
                                <div className="flex group-hover/gen:hidden absolute bottom-1 left-1 right-1 pointer-events-none">
                                    <div className="text-xs text-secondary-foreground/80 p-1 bg-background/80 rounded-md">
                                        <span className="text-[10px] line-clamp-2">{gen.prompt}</span>
                                    </div>
                                </div>
                            )}
                            {gen.model && (
                                <div className="flex group-hover/gen:hidden absolute top-1 left-1 px-1.5 py-0.5 items-center pointer-events-none bg-background/80 rounded-md">
                                    <span className="text-secondary-foreground text-[9px]">{gen.model}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-[0.7rem] text-secondary-foreground/50 bg-secondary/20 rounded px-2 py-1.5 border border-secondary-foreground/10">
                            <div className="shrink-0 text-secondary-foreground/40">{typeIcon(gen.type)}</div>
                            <span className="truncate">{gen.prompt || 'Generation pending...'}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

function TaskPart({ taskId, initialTask }) {
    const { openModal } = useModal()
    const [task, setTask] = useState(initialTask || null)
    const liveState = task?.state || null

    const isActive = liveState && !TERMINAL_STATES.has(liveState)

    useWebSocket(isActive ? ['task_update', 'task_completed', 'task_failed', 'task_cancelled'] : [], {
        onMessage: (message) => {
            if (message.data?.taskId !== taskId) return
            setTask(prev => prev ? { ...prev, ...message.data } : message.data)
        },
    })

    const handleClick = () => {
        if (task) openModal(<TaskModal task={task} />, 'side')
    }

    if (!task) {
        return (
            <div className="flex items-center gap-2 text-[0.7rem] text-secondary-foreground/60 bg-secondary/30 rounded px-2 py-1.5 border border-secondary-foreground/10">
                <LuLoaderCircle className='w-3 h-3 shrink-0  animate-spin' />
                <span>Task {taskId.slice(-6)}...</span>
            </div>
        )
    }

    const stateIcon = liveState === 'completed'
        ? <FiCheckCircle className="w-3 h-3 shrink-0 text-green-400" />
        : liveState === 'failed' || liveState === 'cancelled'
            ? <FiXCircle className="w-3 h-3 shrink-0 text-red-400" />
            : <LuLoaderCircle className='w-2 h-2 shrink-0 animate-spin' />

    const stateColor = liveState === 'completed'
        ? 'text-green-400 bg-green-500/10 border-green-500/20'
        : liveState === 'failed' || liveState === 'cancelled'
            ? 'text-red-400 bg-red-500/10 border-red-500/20'
            : 'text-secondary-foreground/60 bg-secondary/30 border-secondary-foreground/10'

    const modelName = task.model?.name || task.function || null
    const payload = task.request_body?.payload
    const payloadEntries = payload ? Object.entries(payload).filter(([_, v]) => v != null) : []

    return (
        <div onClick={handleClick} className={`text-[0.7rem] rounded px-2.5 py-2 border cursor-pointer hover:brightness-125 transition-all ${stateColor}`}>
            <div className="flex items-center gap-2">
                {stateIcon}
                <span className="font-medium">{modelName || `Task ${taskId?.slice(-6) || ''}`}</span>
                <span className="text-secondary-foreground/40 capitalize">{liveState}</span>
                {task.status && <span className="text-secondary-foreground/30">— {task.status}</span>}
            </div>
            {payloadEntries.length > 0 && (
                <div className="flex flex-col gap-0.5 mt-1.5 ml-5">
                    {payloadEntries.map(([key, value]) => (
                        <div key={key} className="flex gap-1.5 leading-snug">
                            <span className="text-secondary-foreground/40 shrink-0">{key}:</span>
                            <span className="text-secondary-foreground/60 break-all line-clamp-2">
                                {typeof value === 'string' ? value : JSON.stringify(value)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Conversation sidebar
// ---------------------------------------------------------------------------

function ConversationSidebar({ conversations, activeId, onSelect, onCreate, onDelete }) {
    const navigate = useNavigate()

    return (
        <div className='flex flex-col border-r border-r-secondary/60 py-4 px-2 group/conversations w-full max-w-64'>
            <Button type='secondary' size='md' icon={<FiPlus className='w-3 h-3' />} onClick={onCreate} className='flex w-full mb-2'>New Agent</Button>
            <div className='flex flex-col justify-start flex-1 overflow-y-auto'>
                {conversations.map((conv, index) => (
                    <div key={conv._id} className='flex items-center gap-2 justify-between group/conv py-1.5'>
                        <div
                            onClick={() => onSelect(conv._id)}
                            className={`cursor-pointer text-sm flex flex-col ${
                                conv._id === activeId
                                    ? 'text-primary pointer-events-none'
                                    : 'text-secondary-foreground/80 hover:text-secondary-foreground'
                            }`}
                        >

                            <div className='flex items-center gap-2'>
                                {conv.segment && <FiFilm className="w-3.5 h-3.5" />}
                                <span className="truncate select-none">{conv.name || 'New Conversation'}</span>
                            </div>
                            
                        </div>
                        <div className='flex items-center gap-2 opacity-0 group-hover/conv:opacity-100'>
                            <DropdownMenu>
                                <DropdownMenuTrigger>
                                    <div className='flex items-center bg-secondary text-secondary-foreground hover:bg-secondary/80 p-1 text-xs rounded-md'>
                                        <FiMoreHorizontal className='w-3 h-3' />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    <DropdownMenuItem className='p-1 text-xs' onClick={() => { navigator.clipboard.writeText(conv._id); toast('Copied to clipboard') }}>
                                        <FiCopy className='w-4 h-4' />
                                        Copy ID
                                    </DropdownMenuItem>
                                    {conv.episode && conv.segment && <>
                                        <DropdownMenuItem className='p-1 text-xs' onClick={() => navigate(`/show/episodes/${conv.episode}/${conv.segment}/script`)}>
                                            <LuLetterText className='w-4 h-4' />
                                            Script
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className='p-1 text-xs' onClick={() => navigate(`/show/episodes/${conv.episode}/${conv.segment}/storyboard`)}>
                                            <CiViewTimeline className='w-4 h-4' />
                                            Storyboard
                                        </DropdownMenuItem>
                                    </>}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem  className='p-1 text-xs text-red-500' onClick={() => onDelete(conv._id)}>
                                        <FiTrash2 className='w-4 h-4 text-red-500' />
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                ))}
                
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
// Main chat component
// ---------------------------------------------------------------------------

export default function GenAgent({scope = {}, allowReferencedAssets = true}) {
    const [messages, setMessages] = useState([])
    const [prompt, setPrompt] = useState('')
    const [working, setWorking] = useState(false)
    const [customAssets, setCustomAssets] = useState([])

    const [conversations, setConversations] = useState([])
    const [activeConversationId, setActiveConversationId] = useState(null)
    const [creatingConversation, setCreatingConversation] = useState(false)
    const [activeSegment, setActiveSegment] = useState(null)

    const messagesEndRef = useRef(null)
    const inputRef = useRef(null)
    const {show} = useShow()
    const { openModal } = useModal()
    const { subscribe, addListener } = useWebSocketContext()
    const [searchParams] = useSearchParams()
    const conversationIdParam = searchParams.get('conversationId')

    const [systemMessage, setSystemMessage] = useState('')

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    // -----------------------------------------------------------------------
    // Fetch conversations on mount
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!show?._id) return
        const fetchConversations = async () => {
            try {
                const params = new URLSearchParams()
                if (scope.segment) params.set('segment', scope.segment)
                const qs = params.toString()
                const res = await apiFetch(`/api/shows/${show._id}/conversations${qs ? `?${qs}` : ''}`)
                if (!res.ok) return
                const data = await res.json()
                setConversations(data)
                if (data.length > 0 && !activeConversationId) {
                    if (conversationIdParam && data.some(c => c._id === conversationIdParam)) {
                        setActiveConversationId(conversationIdParam)
                    } else {
                        setActiveConversationId(data[0]._id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch conversations:', err)
            }
        }
        fetchConversations()
    }, [show?._id, scope.segment]) // eslint-disable-line react-hooks/exhaustive-deps

    // -----------------------------------------------------------------------
    // Subscribe to the active conversation's WS feed + fetch messages
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!activeConversationId || !show?._id) return

        let cancelled = false

        const fetchMessages = async () => {
            try {
                const res = await apiFetch(`/api/shows/${show._id}/conversations/${activeConversationId}`)
                if (!res.ok || cancelled) return
                const conversation = await res.json()
                if (cancelled) return
                const uiMessages = groupGenerationMessages((conversation.messages || []).map(dbMessageToUiMessage))
                setMessages(uiMessages)
                setCustomAssets(conversation.assets || [])
                setWorking(conversation.state === 'working')
                setActiveSegment(conversation.segment || null)
            } catch (err) {
                console.error('Failed to fetch conversation messages:', err)
            }
        }

        fetchMessages()

        const unsubscribeFeed = subscribe(`conversation/${activeConversationId}`)

        const unsubscribeListener = addListener((message) => {
            if (message?.feed !== `conversation/${activeConversationId}`) return

            const msg = message.data
            if (!msg) return

            

            if (message.event === 'gen_agent_message') {
                if (msg.type === '[DONE]') {
                    setWorking(false)
                    inputRef.current?.focus()
                    return
                }
                if (msg.type === 'conversation_name') {
                    const name = msg.data?.name
                    if (name) setConversations(prev => prev.map(c => c._id === activeConversationId ? { ...c, name } : c))
                    return
                }
                if (msg.type === 'system') {
                    const message = msg.data?.text
                    if (message) setSystemMessage(message)
                    return
                }
                if (msg.type === 'user') return
                setSystemMessage('')
                const uiMessage = dbMessageToUiMessage(msg)
                setMessages(prev => {
                    const exists = prev.some(m =>
                        (m.id && m.id === uiMessage.id) ||
                        (m.type === 'generations' && m.generations?.some(g => g._id === uiMessage.id))
                    )
                    if (exists) {
                        return groupGenerationMessages(prev.map(m => {
                            if (m.id === uiMessage.id) return uiMessage
                            if (m.type === 'generations' && m.generations?.some(g => g._id === uiMessage.id)) {
                                return { ...m, generations: m.generations.map(g => g._id === uiMessage.id ? uiMessage.generation : g) }
                            }
                            return m
                        }))
                    }
                    return groupGenerationMessages([...prev, uiMessage])
                })
            }
        })

        return () => {
            cancelled = true
            unsubscribeFeed()
            unsubscribeListener()
        }
    }, [activeConversationId, show?._id, subscribe, addListener])

    // -----------------------------------------------------------------------
    // Conversation tab handlers
    // -----------------------------------------------------------------------

    const handleSelectConversation = useCallback((convId) => {
        if (convId === activeConversationId) return
        setWorking(false)
        setActiveConversationId(convId)
        setMessages([])
        setCustomAssets([])
        setActiveSegment(null)
        setPrompt('')
        inputRef.current?.focus()
    }, [activeConversationId])

    const handleCreateConversation = useCallback(async (assets) => {
        if (!Array.isArray(assets)) assets = []
        if (!show?._id || creatingConversation) return
        setCreatingConversation(true)
        try {
            const res = await apiFetch(`/api/shows/${show._id}/conversations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: 'New Chat',
                    assets,
                    ...(scope.episode && { episode: scope.episode }),
                    ...(scope.segment && { segment: scope.segment }),
                }),
            })
            if (!res.ok) throw new Error('Failed to create conversation')
            const newConv = await res.json()
            setConversations(prev => [newConv, ...prev])
            setActiveConversationId(newConv._id)
            setMessages([])
            setCustomAssets(newConv.assets || [])
            setPrompt('')
            inputRef.current?.focus()
        } catch (err) {
            console.error('Failed to create conversation:', err)
            toast.error('Failed to create conversation')
        } finally {
            setCreatingConversation(false)
        }
    }, [show?._id, creatingConversation])

    const handleDeleteConversation = useCallback(async (convId) => {
        const conv = conversations.find(c => c._id === convId)
        const confirmed = await openModal(
            <ConfirmModal
                type='destructive'
                title="Delete Conversation"
                message={`Delete "${conv?.name || 'New Conversation'}"? This cannot be undone.`}
            />
        )
        if (!confirmed) return

        try {
            const res = await apiFetch(`/api/shows/${show._id}/conversations/${convId}`, {
                method: 'DELETE',
            })
            if (!res.ok) throw new Error('Failed to delete conversation')

            setConversations(prev => {
                const remaining = prev.filter(c => c._id !== convId)
                if (convId === activeConversationId) {
                    setWorking(false)
                    if (remaining.length > 0) {
                        setActiveConversationId(remaining[0]._id)
                    } else {
                        setActiveConversationId(null)
                        setMessages([])
                        setPrompt('')
                    }
                }
                return remaining
            })
        } catch (err) {
            console.error('Failed to delete conversation:', err)
            toast.error('Failed to delete conversation')
        }
    }, [show?._id, conversations, activeConversationId, openModal])

    // -----------------------------------------------------------------------
    // Custom asset handlers
    // -----------------------------------------------------------------------

    const updateConversationAssets = useCallback(async (newAssets) => {
        if (!show?._id || !activeConversationId) return
        try {
            await apiFetch(`/api/shows/${show._id}/conversations/${activeConversationId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assets: newAssets }),
            })
        } catch (err) {
            console.error('Failed to update conversation assets:', err)
        }
    }, [show?._id, activeConversationId])

    const handleAddCustomAsset = (input) => {
        if (!input) return
        const assets = Array.isArray(input)
            ? input.map(a => ({ url: a.url, type: a.mediaType || getMediaType(a.url) || 'image' }))
            : [{ url: input, type: getMediaType(input) || 'image' }]
        setCustomAssets(prev => {
            const remaining = MAX_CUSTOM_ASSETS - prev.length
            if (remaining <= 0) return prev
            const unique = assets.filter(a => !prev.some(p => p.url === a.url))
            if (unique.length === 0) return prev
            const newAssets = [...prev, ...unique.slice(0, remaining)]
            updateConversationAssets(newAssets)
            return newAssets
        })
    }

    const handleRemoveCustomAsset = (asset) => {
        setCustomAssets(prev => {
            const newAssets = prev.filter(a => a.url !== asset.url)
            updateConversationAssets(newAssets)
            return newAssets
        })
    }

    const handlePreviewCustomAsset = (asset) => {
        const mediaType = asset.type || 'image'
        const previewAsset = { url: asset.url, thumbnail_url: asset.url, name: asset.name || '', mediaType }
        if (mediaType === 'video') openModal(<VideoPreviewModal video={previewAsset} />)
        else if (mediaType === 'audio') openModal(<AudioPreviewModal audio={previewAsset} />)
        else openModal(<ImagePreviewModal image={previewAsset} />)
    }

    // -----------------------------------------------------------------------
    // User input handlers
    // -----------------------------------------------------------------------

    const handleChatKeyDown = (e) => {
        if(e.key === 'Enter' && (e.ctrlKey || e.metaKey)){
            e.preventDefault()
            handleChatSubmit()
        }
    }

    const handleChatSubmit = async () => {
        if (!prompt.trim() || working) return
        if (!activeConversationId) return
        const messageText = prompt.trim()
        setPrompt('')
        setWorking(true)
        if (inputRef.current) {
            inputRef.current.blur()
            inputRef.current.style.height = 'auto'
        }
        setMessages(prev => [...prev, { id: `optimistic-${Date.now()}`, type: 'user', role: 'user', text: messageText }])

        try {
            const res = await apiFetch(`/api/shows/${show._id}/genagent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prompt: messageText,
                    conversationId: activeConversationId,
                    feedId: activeConversationId,
                }),
            })

            if (!res.ok) {
                throw new Error('Failed to send message')
            }
        } catch (error) {
            console.error('Error sending message:', error)
            toast.error('Failed to send message')
            setWorking(false)
        }
    }

    return (
        <div className="flex w-full h-full overflow-hidden relative">
            <ConversationSidebar
                conversations={conversations}
                activeId={activeConversationId}
                onSelect={handleSelectConversation}
                onCreate={handleCreateConversation}
                onDelete={handleDeleteConversation}
            />
            <div className="flex flex-1 flex-col overflow-hidden relative">
                <div className="w-full flex-1 overflow-y-auto flex flex-col items-center px-4 py-6">
                    <div className="flex flex-col gap-4 py-4 max-w-[800px] w-full">
                        {messages.filter(m => m._visible !== false).map((message, index) => (
                            <ChatMessage key={message.id || index} message={message} onNewChat={handleCreateConversation} />
                        ))}
                        {working && (
                            <div className="flex justify-start">
                                <div className="flex items-center gap-2 bg-card rounded-md rounded-bl-none px-4 py-2">
                                    <LuLoaderCircle className='w-3 h-3 shrink-0  text-secondary-foreground/60 animate-spin' />
                                    {systemMessage && <div className="text-secondary-foreground/60 text-xs italic">
                                        {systemMessage}
                                    </div>}
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
                {activeConversationId && (
                    <div className={`px-4 flex flex-col gap-2 items-center justify-center w-full pb-10 ${working ? 'opacity-40 pointer-events-none' : ''}`}>
                        <div className="max-w-[800px] w-full flex flex-col">
                            <div className='flex items-center gap-2 px-2'>
                                {activeSegment?.name && (
                                    <div className="gap-2 bg-card border-x border-t border-secondary rounded-t-md px-2 py-1 -mt-1 flex items-center font-mono text-secondary-foreground/80 text-xs select-none">
                                        <FiFilm className="w-3.5 h-3.5" />
                                        <span>{activeSegment.name}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col p-4 bg-card border border-secondary gap-4 h-auto max-w-[800px] w-full rounded-lg">                                   
                                <textarea ref={inputRef}
                                    value={prompt}
                                    onChange={e => {
                                        setPrompt(e.target.value);
                                        if (e.target) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = `${e.target.scrollHeight}px`;
                                        }
                                    }}
                                    onInput={e => {
                                        if (e.target) {
                                            e.target.style.height = 'auto';
                                            e.target.style.height = `${e.target.scrollHeight}px`;
                                        }
                                    }}
                                    onKeyDown={handleChatKeyDown}
                                    className='font-mono placeholder:text-secondary-foreground/30 text-secondary-foreground p-0 max-h-60 bg-transparent border-none outline-hidden text-sm/5 resize-none' rows={1} placeholder="Let's create something amazing..." 
                                />
                                
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center gap-1'>
                                        {allowReferencedAssets && (
                                            <div className="flex gap-2">
                                                {customAssets.map((asset, i) => (
                                                    <AssetPicker
                                                        key={`${asset.url}-${i}`}
                                                        className="w-12 h-12"
                                                        asset={asset.url}
                                                        mediaType={asset.type || 'image'}
                                                        onSelect={(asset.type || 'image') === 'audio' ? undefined : () => handlePreviewCustomAsset(asset)}
                                                        onRemove={() => handleRemoveCustomAsset(asset)}
                                                        canReplace={false}
                                                        allowPreview={true}
                                                        allowAssetBrowser={false}
                                                        allowUpload={false}
                                                    />
                                                ))}
                                                {customAssets.length < MAX_CUSTOM_ASSETS && (
                                                        <AssetPicker
                                                            className="w-12 h-12"
                                                            onAdd={handleAddCustomAsset}
                                                            allowPreview={false}
                                                            canRemove={false}
                                                            canReplace={false}
                                                            multiSelect
                                                        />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <Button type='primary' icon={<FaArrowUp className='w-4 h-4' />} onClick={handleChatSubmit}/>
                                    </div>
                                   
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
